import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './Contact.css';

// Countries array
const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
  'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
  'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Ivory Coast',
  'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
  'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
  'Oman',
  'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar',
  'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen',
  'Zambia', 'Zimbabwe',
  'Other'
];

interface FormData {
  name: string;
  streetAddress: string;
  city: string;
  country: string;
  phone: string;
  whatsapp: string;
}

interface FormErrors {
  name?: string;
  streetAddress?: string;
  city?: string;
  country?: string;
  phone?: string;
  whatsapp?: string;
}

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    streetAddress: '',
    city: '',
    country: '',
    phone: '',
    whatsapp: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return t('contact.form.name.errors.required');
        if (value.trim().length < 2) return t('contact.form.name.errors.minLength');
        break;
      case 'streetAddress':
        if (!value.trim()) return t('contact.form.streetAddress.errors.required');
        if (value.trim().length < 5) return t('contact.form.streetAddress.errors.minLength');
        break;
      case 'city':
        if (!value.trim()) return t('contact.form.city.errors.required');
        if (value.trim().length < 2) return t('contact.form.city.errors.minLength');
        break;
      case 'country':
        if (!value.trim()) return t('contact.form.country.errors.required');
        break;
      case 'phone':
        if (!value.trim()) return t('contact.form.phone.errors.required');
        // Use the provided regex for international phone number validation
        if (!/^\+((?:9[679]|8[035789]|6[789]|5[90]|42|3[578]|2[1-689])|9[0-58]|8[1246]|6[0-6]|5[1-8]|4[013-9]|3[0-469]|2[70]|7|1)(?:\W*\d){0,13}\d$/.test(value.trim())) {
          return t('contact.form.phone.errors.invalid');
        }
        break;
      case 'whatsapp':
        if (value.trim() && !/^\+((?:9[679]|8[035789]|6[789]|5[90]|42|3[578]|2[1-689])|9[0-58]|8[1246]|6[0-6]|5[1-8]|4[013-9]|3[0-469]|2[70]|7|1)(?:\W*\d){0,13}\d$/.test(value.trim())) {
          return t('contact.form.whatsapp.errors.invalid');
        }
        break;
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    Object.keys(formData).forEach(key => {
      if (key !== 'whatsapp') {
        const error = validateField(key, formData[key as keyof FormData]);
        if (error) {
          newErrors[key as keyof FormErrors] = error;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check form validity without setting state (to avoid infinite re-renders)
  const checkFormValidity = (): boolean => {
    // Check if all required fields are filled
    const requiredFieldsFilled = formData.name.trim() !== '' &&
           formData.streetAddress.trim() !== '' &&
           formData.city.trim() !== '' &&
           formData.country.trim() !== '' &&
           formData.phone.trim() !== '';
    
    if (!requiredFieldsFilled) {
      return false;
    }
    
    // Check if there are any validation errors without setting state
    const newErrors: FormErrors = {};
    
    Object.keys(formData).forEach(key => {
      if (key !== 'whatsapp') {
        const error = validateField(key, formData[key as keyof FormData]);
        if (error) {
          newErrors[key as keyof FormErrors] = error;
        }
      }
    });
    
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return checkFormValidity();
  };

  // Check if a field should show error (either touched or form is being validated)
  const shouldShowError = (fieldName: string): boolean => {
    const hasError = errors[fieldName as keyof FormErrors];
    const isTouched = touched[fieldName];
    const isFormBeingValidated = Object.keys(errors).length > 0; // If any errors exist, form is being validated
    
    return Boolean(hasError && (isTouched || isFormBeingValidated));
  };

  const handleSubmit = (e: React.FormEvent) => {
    // Mark all fields as touched
    setTouched({
      name: true,
      streetAddress: true,
      city: true,
      country: true,
      phone: true,
      whatsapp: true
    });

    if (!validateForm()) {
      // Form is invalid, prevent submission
      e.preventDefault();
      return false;
    }
    
    // Form is valid, allow submission to proceed
    return true;
  };

  const getFieldClassName = (fieldName: string) => {
    const hasError = errors[fieldName as keyof FormErrors];
    const isTouched = touched[fieldName];
    
    if (hasError && isTouched) {
      return 'form-group error';
    }
    return 'form-group';
  };

  return (
    <section id="contact" className="contact section" role="region" aria-labelledby="contact-title">
      <div className="container">
        <h2 id="contact-title" className="section-title">{t('contact.title')}</h2>
        <p className="section-subtitle">
          {t('contact.subtitle')}
        </p>

        <div className="contact-content">
          <form 
            className="contact-form" 
            action={`https://formsubmit.co/${process.env.REACT_APP_FORMSUBMIT_EMAIL}`}
            method="POST"
            onSubmit={handleSubmit}
          >
            {/* FormSubmit hidden fields */}
            <input type="hidden" name="_subject" value="New Car Parts Business Inquiry" />
            <input type="hidden" name="_next" value={`${process.env.REACT_APP_WEBSITE_LINK}/thanks`} />
            <input type="hidden" name="_template" value="table" />
            
            {/* Honeypot field to prevent spam */}
            <input type="text" name="_honey" style={{ display: 'none' }} />

            <div className="form-row">
              <div className={getFieldClassName('name')}>
                <label htmlFor="name">{t('contact.form.name.label')}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder={t('contact.form.name.placeholder')}
                  required
                />
                {shouldShowError('name') && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>
            </div>

            <div className="form-row street-address-row">
              <div className={getFieldClassName('streetAddress')}>
                <label htmlFor="streetAddress">{t('contact.form.streetAddress.label')}</label>
                <input
                  type="text"
                  id="streetAddress"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder={t('contact.form.streetAddress.placeholder')}
                  required
                />
                {shouldShowError('streetAddress') && (
                  <span className="error-message">{errors.streetAddress}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className={getFieldClassName('city')}>
                <label htmlFor="city">{t('contact.form.city.label')}</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder={t('contact.form.city.placeholder')}
                  required
                />
                {shouldShowError('city') && (
                  <span className="error-message">{errors.city}</span>
                )}
              </div>
              <div className={getFieldClassName('country')}>
                <label htmlFor="country">{t('contact.form.country.label')}</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                >
                  <option value="">{t('contact.form.country.placeholder')}</option>
                  {countries.map((country, index) => (
                    <option key={index} value={country}>{country}</option>
                  ))}
                </select>
                {shouldShowError('country') && (
                  <span className="error-message">{errors.country}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className={getFieldClassName('phone')}>
                <label htmlFor="phone">{t('contact.form.phone.label')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder={t('contact.form.phone.placeholder')}
                  required
                />
                {shouldShowError('phone') && (
                  <span className="error-message">{errors.phone}</span>
                )}
              </div>
              <div className={getFieldClassName('whatsapp')}>
                <label htmlFor="whatsapp">{t('contact.form.whatsapp.label')}</label>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder={t('contact.form.whatsapp.placeholder')}
                />
                {shouldShowError('whatsapp') && (
                  <span className="error-message">{errors.whatsapp}</span>
                )}
              </div>
            </div>

            <button 
              type="submit" 
              className="btn submit-btn"
              disabled={!isFormValid()}
            >
              {t('contact.form.submit')}
            </button>
          </form>

          <div className="contact-info-card">
            <div className="contact-info-header">
              <h3>{t('contact.info.title')}</h3>
              <p className="contact-info-subtitle">{t('contact.info.subtitle')}</p>
            </div>
            
            <div className="contact-methods">
              <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`} className="contact-method">
                <div className="contact-method-icon">
                  <i className="icon">📧</i>
                </div>
                <div className="contact-method-content">
                  <span className="contact-value">{process.env.REACT_APP_CONTACT_EMAIL}</span>
                </div>
              </a>
              
              <a href={`tel:${process.env.REACT_APP_CONTACT_PHONE?.replace(/\s/g, '')}`} className="contact-method">
                <div className="contact-method-icon">
                  <i className="icon">📞</i>
                </div>
                <div className="contact-method-content">
                  <span className="contact-value">{process.env.REACT_APP_CONTACT_PHONE}</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
